import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Armchair, Clock, DollarSign, Download, Eye, Truck } from 'lucide-react';
import { capitalize, docTitle, formatNumber, invoiceBadgeClass, invoiceHref } from './badgeUtils';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'merch', label: 'Merchandise' },
  { key: 'time', label: 'Time Billing' },
];

export default function InvoicesTab({ invoices, tbInvoices, projectId }) {
  const [filter, setFilter] = useState('all');

  const stats = useMemo(() => {
    const timeBillingTotal = tbInvoices.reduce(
      (sum, inv) => sum + Number(inv.totals?.total || 0),
      0
    );
    const merchandiseTotal = invoices.reduce((sum, inv) => sum + Number(inv.totals?.total || 0), 0);
    const balanceDue =
      invoices.reduce((sum, inv) => sum + Number(inv.remainingBalance || 0), 0) +
      tbInvoices.reduce((sum, inv) => sum + Number(inv.remainingBalance || 0), 0);
    return { timeBillingTotal, merchandiseTotal, balanceDue };
  }, [invoices, tbInvoices]);

  if (invoices.length === 0 && tbInvoices.length === 0) {
    return <p className="cp-empty">No invoices yet.</p>;
  }

  const showMerch = filter === 'all' || filter === 'merch';
  const showTime = filter === 'all' || filter === 'time';

  return (
    <div>
      <div className="cp-pill-row">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            className={`cp-pill ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="cp-stats-card">
        <div className="cp-stat-item">
          <div className="cp-stat-icon cp-stat-icon-gray">
            <Clock size={19} />
          </div>
          <div>
            <div className="cp-stat-value">{formatNumber(stats.timeBillingTotal)}</div>
            <div className="cp-stat-label">Total Time Billing</div>
          </div>
        </div>
        <div className="cp-stat-item">
          <div className="cp-stat-icon cp-stat-icon-tan">
            <Armchair size={19} />
          </div>
          <div>
            <div className="cp-stat-value">{formatNumber(stats.merchandiseTotal)}</div>
            <div className="cp-stat-label">Total Merchandise</div>
          </div>
        </div>
        <div className="cp-stat-item">
          <div className="cp-stat-icon cp-stat-icon-coral">
            <DollarSign size={19} />
          </div>
          <div>
            <div className="cp-stat-value">{formatNumber(stats.balanceDue)}</div>
            <div className="cp-stat-label">Balance Due</div>
          </div>
        </div>
      </div>

      {showMerch && invoices.length > 0 && (
        <div className="cp-doc-list">
          {invoices.map((inv) => (
            <InvoiceRow
              key={inv.invoiceId}
              icon={<Truck size={20} />}
              number={inv.invoiceNumber || 'Invoice'}
              title={
                inv.title ||
                docTitle(inv.lineItems, inv.invoiceNumber ? `Invoice ${inv.invoiceNumber}` : 'Invoice')
              }
              date={inv.invoiceDate}
              payment={inv.totals?.paid}
              balance={inv.remainingBalance}
              status={inv.status}
              badgeClass={invoiceBadgeClass(inv.status)}
              {...invoiceHref(inv, projectId)}
            />
          ))}
        </div>
      )}

      {showTime && tbInvoices.length > 0 && (
        <div className="cp-doc-list">
          {tbInvoices.map((inv) => {
            const isPaid = inv.remainingBalance != null && inv.remainingBalance <= 0;
            return (
              <InvoiceRow
                key={inv.tbInvoiceId}
                icon={<Clock size={20} />}
                number={inv.invoiceNumber || 'Time Invoice'}
                title={
                  inv.invoiceNumber
                    ? `Time Billing Invoice ${inv.invoiceNumber}`
                    : 'Time Billing Invoice'
                }
                date={inv.createdAt}
                payment={
                  inv.totals?.total != null ? inv.totals.total - (inv.remainingBalance || 0) : null
                }
                balance={inv.remainingBalance}
                status={isPaid ? 'paid' : 'unpaid'}
                badgeClass={isPaid ? 'cp-badge-green' : 'cp-badge-red'}
                href={
                  inv.shareToken
                    ? `/public/timebilling-invoice/${inv.shareToken}`
                    : `/client/projects/${projectId}/timebilling-invoices/${inv.tbInvoiceId}`
                }
                isExternal={Boolean(inv.shareToken)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function InvoiceRow({
  icon,
  number,
  title,
  date,
  payment,
  balance,
  status,
  badgeClass,
  href,
  isExternal,
}) {
  const hasBalance = Number(balance || 0) > 0;

  const inner = (
    <>
      <div className="cp-doc-icon">{icon}</div>

      <div className="cp-doc-body">
        <div className="cp-doc-number-row">
          <span>{number}</span>
          <Download size={12} />
        </div>
        <p className="cp-doc-title">{title}</p>
        <div className="cp-doc-meta-row">
          {date && <span>Date: {new Date(date).toLocaleDateString()}</span>}
        </div>
      </div>

      <div className="cp-doc-amounts">
        <div className="cp-doc-amount-col">
          <div className="cp-doc-amount-value">{formatNumber(payment)}</div>
          <div className="cp-doc-amount-label">Payment</div>
        </div>
        <div className="cp-doc-amount-col">
          <div className="cp-doc-amount-value">{formatNumber(balance)}</div>
          <div className="cp-doc-amount-label">Balance</div>
        </div>
      </div>

      <div className="cp-doc-status">
        <span className={`cp-badge ${badgeClass}`}>{capitalize(status || 'draft')}</span>
        {hasBalance ? (
          <span className="cp-btn-primary" style={{ padding: '7px 16px', fontSize: 13 }}>
            Pay Now
          </span>
        ) : (
          <span className="cp-btn-outline" style={{ padding: '7px 16px', fontSize: 13 }}>
            <Eye size={13} /> View
          </span>
        )}
      </div>
    </>
  );

  return isExternal ? (
    <a href={href} target="_blank" rel="noopener noreferrer" className="cp-doc-card">
      {inner}
    </a>
  ) : (
    <Link to={href} className="cp-doc-card">
      {inner}
    </Link>
  );
}
